/**
 * Video generation: dispatcher + poller for the async background job
 * workflow. POST /content-generation/video/generate returns HTTP 202 with
 * status="pending"; the final S3 video URL surfaces only via
 * GET /content-generation/video/status/{post_id}. This module keeps that
 * orchestration framework-agnostic so it can be unit-tested against a
 * mocked API client. Mirrors carouselGeneration.ts exactly.
 */
import type {
  VideoAlreadyGeneratedResponse,
  VideoGenerateRequest,
  VideoGenerateResponse,
} from '../types/video';

export const VIDEO_POLL_INTERVAL_MS = 5000;
// Video is the slowest SMM generation type: a render usually takes 4-5
// minutes, but FAL's queue can push one past 10 (the backend waits up to 12
// for it), and a quick render that fails the quality check is rendered once
// more. 15 min covers that; past it the backend is still working and the
// editor offers "Check again" instead of showing a failure.
export const VIDEO_POLL_TIMEOUT_MS = 15 * 60 * 1000;
export const VIDEO_MAX_CONSECUTIVE_ERRORS = 3;

/** Minimal surface of the API client this service needs. */
export interface VideoApiClient {
  generateVideo(
    request: VideoGenerateRequest
  ): Promise<VideoGenerateResponse | VideoAlreadyGeneratedResponse>;
  getVideoStatus(postId: string): Promise<VideoGenerateResponse>;
}

export interface DispatchResult {
  /** Resolved post_id the background job runs against. */
  postId: string;
  /** Raw POST response — status is expected to be "pending" on 202. */
  response: VideoGenerateResponse | VideoAlreadyGeneratedResponse;
}

/**
 * Dispatch the generation job. Resolves once the POST returns (202), i.e.
 * the job has been accepted and runs in the background — it does NOT wait
 * for the video. Throws on transport/HTTP errors so callers can surface a
 * message.
 */
export async function dispatchVideoGeneration(
  api: VideoApiClient,
  request: VideoGenerateRequest
): Promise<DispatchResult> {
  const response = await api.generateVideo(request);
  const postId = response && 'post_id' in response ? response.post_id : request.post_id;
  return { postId, response };
}

export interface PollVideoStatusOptions {
  postId: string;
  /** Cadence between status checks. Defaults to 5s. */
  intervalMs?: number;
  /** Max wait before giving up and firing onTimeout. Defaults to 6 min. */
  timeoutMs?: number;
  /** Consecutive HTTP/network errors tolerated before onError. Defaults to 3. */
  maxConsecutiveErrors?: number;
  /** Fired on every successful status read (useful for debug/tests). */
  onStatus?: (status: VideoGenerateResponse) => void;
  /** status === "done" with a non-empty output_paths[0]. */
  onDone: (videoUrl: string) => void;
  /** status === "error", a 404 (post missing), or too many consecutive polling errors. */
  onError: (message: string) => void;
  /** Polled for the full timeout window without a terminal state. */
  onTimeout: () => void;
}

/**
 * Poll the status endpoint until the job reaches a terminal state, the
 * timeout elapses, or cancellation. Returns a cancel() handle. Same
 * stopping semantics as pollCarouselStatus.
 */
export function pollVideoGenerationStatus(
  api: VideoApiClient,
  options: PollVideoStatusOptions
): () => void {
  const {
    postId,
    intervalMs = VIDEO_POLL_INTERVAL_MS,
    timeoutMs = VIDEO_POLL_TIMEOUT_MS,
    maxConsecutiveErrors = VIDEO_MAX_CONSECUTIVE_ERRORS,
    onStatus,
    onDone,
    onError,
    onTimeout,
  } = options;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveErrors = 0;
  const startedAt = Date.now();

  const stop = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const isExpired = () => Date.now() - startedAt >= timeoutMs;

  const scheduleNext = () => {
    if (cancelled) return;
    timer = setTimeout(() => { void tick(); }, intervalMs);
  };

  const tick = async () => {
    if (cancelled) return;
    if (isExpired()) {
      onTimeout();
      stop();
      return;
    }
    try {
      const status = await api.getVideoStatus(postId);
      if (cancelled) return;
      consecutiveErrors = 0;
      onStatus?.(status);

      if (status.status === 'done') {
        const videoUrl = status.output_paths?.[0];
        if (videoUrl) {
          onDone(videoUrl);
        } else {
          onError('Generation completed, but no video URL was returned.');
        }
        stop();
        return;
      }

      if (status.status === 'error') {
        onError(status.error_message || 'Generation failed. Your credits were refunded automatically.');
        stop();
        return;
      }

      // status === "pending" — worker still running
      scheduleNext();
    } catch (err: any) {
      if (cancelled) return;
      // Post no longer exists — nothing to wait on, stop immediately.
      if (err?.response?.status === 404) {
        onError('This post could not be found. Generation could not be completed.');
        stop();
        return;
      }
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        onError(
          'Could not check the generation status. Please try again in a moment.'
        );
        stop();
        return;
      }
      scheduleNext();
    }
  };

  void tick();
  return stop;
}
